"use client";

import { useReducedMotion } from "framer-motion";
import { TextReveal } from "@/components/motion/TextReveal";
import { cn } from "@/lib/utils";

interface HeroHeadingProps {
  lines: readonly string[];
  variant?: "default" | "onPhoto";
  suppressEntrance?: boolean;
  scene8Reveal?: boolean;
  compact?: boolean;
}

export function HeroHeading({
  lines,
  variant = "default",
  suppressEntrance = false,
  scene8Reveal = false,
  compact = false,
}: HeroHeadingProps) {
  const reduced = useReducedMotion();
  const onPhoto = variant === "onPhoto";

  const h1Class = cn(
    "font-display font-bold leading-[1.1] tracking-tight",
    compact
      ? "text-[0.65rem] sm:text-xs"
      : "text-3xl sm:text-4xl md:text-[2.5rem] lg:text-5xl",
    onPhoto ? "hero-headline-shadow" : "text-white drop-shadow-md"
  );

  const shouldAnimate =
    !reduced && (scene8Reveal || !suppressEntrance);

  if (!shouldAnimate) {
    return (
      <h1 className={h1Class}>
        {lines.map((line, index) => (
          <span
            key={`${index}-${line}`}
            className={cnLine(index, lines.length, onPhoto, compact)}
          >
            {index < lines.length - 1 ? (
              <>
                {line}
                <br />
              </>
            ) : (
              line
            )}
          </span>
        ))}
      </h1>
    );
  }

  const lineDelayBase = scene8Reveal ? 0.08 : 0.12;
  const charStagger = scene8Reveal ? 0.018 : 0.028;

  return (
    <h1 className={h1Class} key={scene8Reveal ? "scene8" : "reveal"}>
      {lines.map((line, index) => (
        <span key={`${index}-${line}`}>
          <TextReveal
            text={line}
            as="span"
            className={cnLine(index, lines.length, onPhoto, compact)}
            trigger="mount"
            delay={index * lineDelayBase}
            charStagger={charStagger}
          />
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </h1>
  );
}

function cnLine(index: number, total: number, onPhoto: boolean, compact = false) {
  if (compact) {
    if (index === total - 1) {
      return "block text-[#2E7D32]";
    }
    return "block text-[#3d3428]";
  }

  if (index === total - 1) {
    return onPhoto
      ? "block text-[var(--hero-accent)] hero-headline-shadow"
      : "block text-[#4CAF50]";
  }

  return onPhoto
    ? "block text-[var(--hero-headline)] hero-headline-shadow"
    : "block text-white";
}
